import { useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { NavTabProps } from '../components/NavTabs'
import { useCanNavigateTo } from './useCanNavigateTo'

type UseAuthorizedTabsOptions = {
  /** When true, auto-navigates to the first authorized tab if the current path doesn't match any tab. */
  redirectToFirstTab?: boolean
  /**
   * If undefined, the consuming page is not using any async data to determine which tabs to show, so the hook can perform the redirect logic immediately.
   * If false, the hook will wait until `ready` is true before performing any redirect logic.
   */
  ready?: boolean
}

type UseAuthorizedTabsResult = {
  tabs: NavTabProps['tabs']
  /** True when redirectToFirstTab is enabled but no tabs are authorized. */
  isUnauthorized: boolean
}

/**
 * Filters tabs based on whether the user has access to each tab's destination route.
 * Resolves each tab's `to` path against the route tree and checks the `auth` handle
 * on all matched routes in the chain. No scope info needed on the tabs themselves.
 *
 * Optionally redirects to the first authorized tab when the current path doesn't match any tab.
 * When no tabs are authorized, sets `isUnauthorized` to true so the consumer can render
 * an inline unauthorized view without changing the URL.
 */
export function useAuthorizedTabs(tabs: NavTabProps['tabs'], options?: UseAuthorizedTabsOptions): UseAuthorizedTabsResult {
  const isAuthorized = useCanNavigateTo()
  const navigate = useNavigate()
  const location = useLocation()

  const authorizedTabs = useMemo(() => tabs.filter(tab => isAuthorized(tab.to)), [tabs, isAuthorized])

  const isUnauthorized = !!options?.redirectToFirstTab && options?.ready !== false && authorizedTabs.length === 0

  useEffect(() => {
    if (!options?.redirectToFirstTab) return
    if (options?.ready === false) return
    if (authorizedTabs.length === 0) return

    const isOnValidTab = authorizedTabs.some(tab => location.pathname.endsWith(tab.to) || location.pathname.includes(`${tab.to}/`))

    if (!isOnValidTab) {
      navigate(authorizedTabs[0].to, { replace: true })
    }
  }, [authorizedTabs, location.pathname, navigate, options?.redirectToFirstTab, options?.ready])

  return { tabs: authorizedTabs, isUnauthorized }
}
